import { dateFormatterDayMonthYearLong2Digits } from "@qivia/ui";
import { AdvancedAnalyticsVehicles } from "../../fleet/fleetApi";
import {
  DriverPlanningByDay,
  PlanningDetailsType,
  Trips,
  TripsAndPlanningsHistory,
  TripsDetails,
} from "./tripsApi";

const MURFY_COMPANY_UUID = "99fd3169-f6f9-45ce-9aab-87c8664ac206";
const SARMATES_COMPANY_UUID = "ec02e593-f37d-4884-91d2-3a28b9645fc2";

export const TRIP_ADDRESSES_ALLOWED_COMPANY_UUIDS = [
  MURFY_COMPANY_UUID,
  SARMATES_COMPANY_UUID,
];

export const getTripDurationVariation = (
  trip: TripsDetails,
  tripsPlanning: PlanningDetailsType | null,
) => {
  if (!trip.secondsDuration || !tripsPlanning?.endTime) return null;

  return Number(
    Number(
      trip.secondsDuration -
        (new Date(tripsPlanning?.endTime).getTime() -
          new Date(tripsPlanning?.startTime).getTime()) /
          1000,
    ).toFixed(2),
  );
};

export const getTripDurationVariationPercentage = (
  trip: TripsDetails,
  tripsPlanning: PlanningDetailsType | null,
) => {
  if (!trip.secondsDuration || !tripsPlanning?.endTime) return null;
  const tripDurationVariation = getTripDurationVariation(trip, tripsPlanning);
  if (!tripDurationVariation) return null;

  return (
    tripDurationVariation /
    (new Date(tripsPlanning?.endTime).getTime() -
      new Date(tripsPlanning?.startTime).getTime()) /
    1000
  );
};

export const getTripDistanceVariation = (
  trip: TripsDetails,
  tripsPlanning: PlanningDetailsType | null,
) => {
  if (!trip.distance || !tripsPlanning?.expectedMileage) return null;

  return Number(
    Number(trip.distance - tripsPlanning.expectedMileage).toFixed(2),
  );
};

export const getTripDistanceVariationPercentage = (
  trip: TripsDetails,
  tripsPlanning: PlanningDetailsType | null,
) => {
  if (!trip.distance || !tripsPlanning?.expectedMileage) return null;
  const tripDistanceVariation = getTripDistanceVariation(trip, tripsPlanning);
  if (!tripDistanceVariation) return null;

  return tripDistanceVariation / tripsPlanning.expectedMileage;
};

export const getTotalDistanceVariation = (
  trips: Trips,
  plannings: DriverPlanningByDay,
) => {
  const tripDistance = trips.totalDistanceInKm;
  const planningDistance = plannings.expectedMileagePerDay;

  return tripDistance - planningDistance;
};

export const getTotalDistanceVariationPercentage = (
  trips: Trips,
  plannings: DriverPlanningByDay,
) => {
  const tripDistance = trips.totalDistanceInKm;
  const planningDistance = plannings.expectedMileagePerDay;

  const variation = Number(
    ((tripDistance - planningDistance) / planningDistance).toFixed(2),
  );

  return variation * 100;
};

export const getTotalDurationVariation = (
  trips: Trips,
  plannings: DriverPlanningByDay,
) => {
  const tripSecondsDuration = trips.hoursDuration * 3600;
  const planningSecondsDuration = plannings.expectedDurationPerDay;

  return Number(
    ((tripSecondsDuration - planningSecondsDuration) / 3600).toFixed(2),
  );
};

export const getTotalDurationVariationPercentage = (
  trips: Trips,
  plannings: DriverPlanningByDay,
) => {
  const tripSecondsDuration = trips.hoursDuration * 3600;
  const planningSecondsDuration = plannings.expectedDurationPerDay;

  const variation = Number(
    (
      (tripSecondsDuration - planningSecondsDuration) /
      planningSecondsDuration
    ).toFixed(2),
  );

  return variation * 100;
};

export const getTripsAndPlanningsHistory = (payload: {
  trips: Trips[];
  plannings: DriverPlanningByDay[];
}): Omit<TripsAndPlanningsHistory, "departmentName">[] => {
  // Création d'un Map indexé par driverIdNumber + date formatée
  const startTime = new Date();
  const planningsMap = payload.plannings.reduce((map, planning) => {
    if (!planning.driverIdNumber) return map;
    const dateKey = dateFormatterDayMonthYearLong2Digits(
      new Date(planning.date),
    );
    const key = `${planning.driverIdNumber}|${dateKey}`;
    map.set(key, planning);
    return map;
  }, new Map<string, DriverPlanningByDay>());

  const endMapDate = new Date();
  console.log(
    `[getTripsAndPlanningsHistory] Création du Map des plannings: ${endMapDate.getTime() - startTime.getTime()}ms`,
  );
  const startTripsMap = new Date();
  const result = payload.trips.map((t) => {
    const driverUuid = t.driver?.uuid ?? "";
    const driverAnalyticCode1 = t.driver?.analyticCode1 ?? "";
    const tripDateKey = dateFormatterDayMonthYearLong2Digits(new Date(t.date));
    const tripPlanningKey = `${driverAnalyticCode1}|${tripDateKey}`;

    const associatedPlanning = driverAnalyticCode1
      ? (planningsMap.get(tripPlanningKey) ?? null)
      : null;

    return {
      date: t.date,
      count: t.count,
      registrationNumber: t.registrationNumber,
      reference: t.reference,
      usageStartDate: t.usageStartDate,
      usageEndDate: t.usageEndDate,
      totalDistanceInKm: t.totalDistanceInKm,
      hoursDuration: t.hoursDuration,
      vehicleUuid: t.vehicleUuid,
      vehicleModel: t.vehicleModel,
      vehicleType: t.vehicleType,
      vehicleAnalyticCode: t.vehicleAnalyticCode,
      trips: t.trips,
      // Nouvelles propriétés
      driverName:
        (t.driver?.firstName ?? "") +
        " " +
        (t.driver?.lastName ?? "") +
        " " +
        (t.driver?.analyticCode1 ?? ""),
      driverCity: associatedPlanning?.driverCity ?? null,
      driverAnalyticCode1: t.driver?.analyticCode1 ?? "",
      driverUuid,
      associatedPlanning,
      distanceVariation: associatedPlanning
        ? getTotalDistanceVariation(t, associatedPlanning)
        : t.totalDistanceInKm > 0
          ? t.totalDistanceInKm
          : null,
      distanceVariationPercent: associatedPlanning
        ? getTotalDistanceVariationPercentage(t, associatedPlanning)
        : t.totalDistanceInKm > 0
          ? 100
          : null,
    };
  });
  const endTripsMap = new Date();
  console.log(
    `[getTripsAndPlanningsHistory] Mapping des trips: ${endTripsMap.getTime() - startTripsMap.getTime()}ms`,
  );
  console.log(
    `[getTripsAndPlanningsHistory] Fin - Temps total: ${endTripsMap.getTime() - startTime.getTime()}ms`,
  );
  return result;
};

export const getAdvancedAnalyticsVehiclesGroupByVehicle = (
  vehicles: AdvancedAnalyticsVehicles[],
) =>
  vehicles.reduce(
    (acc, v) => ({
      ...acc,
      [v.uuid]: v,
    }),
    {} as Record<string, AdvancedAnalyticsVehicles>,
  );
